import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { upsertCchiPolicy, uploadCchiPolicyDocument } from '../../../api/Payroll/cchi';
import { EmployeeHealthInsuranceView, EmployeeHealthInsuranceInput, CchiPlanTier } from '../../../models/payroll';
import { DatePicker as CustomDatePicker, formatDateForInput } from '../../../components/dateTimePickers';

interface CchiPolicyEditorProps {
  userId: string;
  mode: 'create' | 'edit' | 'renew';
  initial: EmployeeHealthInsuranceView | null;
  onClose: () => void;
  onSaved: (updated: EmployeeHealthInsuranceView) => void;
}

const TIERS: CchiPlanTier[] = ['ClassC', 'ClassB', 'ClassA', 'Vip'];

export default function CchiPolicyEditor({ userId, mode, initial, onClose, onSaved }: CchiPolicyEditorProps) {
  const { t } = useTranslation();
  const seed: EmployeeHealthInsuranceInput = {
    userId,
    providerName: mode === 'renew' ? initial?.providerName ?? null : initial?.providerName ?? null,
    policyNumber: mode === 'renew' ? initial?.policyNumber ?? null : initial?.policyNumber ?? null,
    planTier: (initial?.planTier as CchiPlanTier | undefined) ?? null,
    effectiveDate: mode === 'renew' ? null : initial?.effectiveDate ?? null,
    expiryDate: mode === 'renew' ? null : initial?.expiryDate ?? null,
    monthlyPremium: initial?.monthlyPremium ?? null,
    employeePremiumShare: initial?.employeePremiumShare ?? null,
    policyDocumentUrl: mode === 'renew' ? null : initial?.policyDocumentUrl ?? null,
    waiverReason: initial?.waiverReason ?? null,
  };
  const [form, setForm] = useState<EmployeeHealthInsuranceInput>(seed);
  const [showWaiver, setShowWaiver] = useState(!!seed.waiverReason);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [tierWarning, setTierWarning] = useState<{ expectedTier: string; actualTier: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    setTierWarning(null);
    try {
      let documentUrl = form.policyDocumentUrl;
      if (file) {
        const uploadRes = await uploadCchiPolicyDocument(userId, file);
        if (uploadRes.errors?.length) {
          setError(uploadRes.errors[0].message);
          setSaving(false);
          return;
        }
        documentUrl = uploadRes.payload?.url ?? null;
      }
      const payload: EmployeeHealthInsuranceInput = {
        ...form,
        userId,
        policyDocumentUrl: documentUrl,
        waiverReason: showWaiver ? form.waiverReason : null,
      };
      const res = await upsertCchiPolicy(userId, payload);
      if (res.errors?.length) {
        setError(res.errors[0].message);
        setSaving(false);
        return;
      }
      const updated = res.payload!;
      if (updated.tierWarning) {
        setTierWarning(updated.tierWarning);
      }
      onSaved(updated);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-end bg-black/30"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="h-full w-full max-w-md bg-background p-6 overflow-y-auto shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">{t('payroll.cchi.editor.title')}</h2>
          <button type="button" onClick={onClose} aria-label={t('Close')} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        {tierWarning && (
          <div className="mb-3 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
            {t('payroll.cchi.editor.tierMismatch', { expectedTier: tierWarning.expectedTier, actualTier: tierWarning.actualTier })}
          </div>
        )}
        {error && (
          <div className="mb-3 rounded bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</div>
        )}

        <div className="space-y-3 text-xs">
          <Field label={t('payroll.cchi.editor.fields.provider')}>
            <input type="text" value={form.providerName ?? ''} onChange={e => setForm({ ...form, providerName: e.target.value || null })} className="w-full rounded border bg-background px-3 py-2" />
          </Field>
          <Field label={t('payroll.cchi.editor.fields.policyNumber')}>
            <input type="text" value={form.policyNumber ?? ''} onChange={e => setForm({ ...form, policyNumber: e.target.value || null })} className="w-full rounded border bg-background px-3 py-2" />
          </Field>
          <Field label={t('payroll.cchi.editor.fields.planTier')}>
            <select value={form.planTier ?? ''} onChange={e => setForm({ ...form, planTier: (e.target.value || null) as CchiPlanTier | null })} className="w-full rounded border bg-background px-3 py-2">
              <option value="">—</option>
              {TIERS.map(tier => (
                <option key={tier} value={tier}>{t(`payroll.cchi.planTier.${tier}`)}</option>
              ))}
            </select>
          </Field>
          <Field label={t('payroll.cchi.editor.fields.effectiveDate')}>
            <CustomDatePicker label="" value={form.effectiveDate?.slice(0, 10) ?? null} onChange={(d: any) => setForm({ ...form, effectiveDate: d ? formatDateForInput(d) : null })} />
          </Field>
          <Field label={t('payroll.cchi.editor.fields.expiryDate')}>
            <CustomDatePicker label="" value={form.expiryDate?.slice(0, 10) ?? null} onChange={(d: any) => setForm({ ...form, expiryDate: d ? formatDateForInput(d) : null })} />
          </Field>
          <Field label={t('payroll.cchi.editor.fields.monthlyPremium')}>
            <input type="number" step="0.01" value={form.monthlyPremium ?? ''} onChange={e => setForm({ ...form, monthlyPremium: e.target.value === '' ? null : Number(e.target.value) })} className="w-full rounded border bg-background px-3 py-2" />
          </Field>
          <Field label={t('payroll.cchi.editor.fields.employeePremiumShare')}>
            <input type="number" step="0.01" value={form.employeePremiumShare ?? ''} onChange={e => setForm({ ...form, employeePremiumShare: e.target.value === '' ? null : Number(e.target.value) })} className="w-full rounded border bg-background px-3 py-2" />
          </Field>
          <Field label={t('payroll.cchi.editor.fields.documentUpload')}>
            <input type="file" accept="application/pdf" onChange={e => setFile(e.target.files?.[0] ?? null)} className="w-full text-xs" />
          </Field>

          <div className="border-t border-border pt-3">
            <label className="inline-flex items-center gap-2">
              <input type="checkbox" checked={showWaiver} onChange={e => setShowWaiver(e.target.checked)} />
              <span>{t('payroll.cchi.editor.markAsWaived')}</span>
            </label>
            {showWaiver && (
              <>
                <div className="mt-2 rounded bg-amber-50 px-2 py-1 text-xs text-amber-900">
                  {t('payroll.cchi.editor.waiver.warning')}
                </div>
                <textarea
                  value={form.waiverReason ?? ''}
                  onChange={e => setForm({ ...form, waiverReason: e.target.value || null })}
                  rows={2}
                  placeholder={t('payroll.cchi.editor.fields.waiverReason')}
                  className="mt-2 w-full rounded border bg-background px-3 py-2"
                />
              </>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs rounded border border-border">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="px-3 py-2 text-xs rounded bg-primary text-primary-foreground disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium">{label}</label>
      {children}
    </div>
  );
}
